import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import {
  Autocomplete,
  Button,
  Datepicker,
  FieldWarning,
  GridContainer,
  GridItem,
  TextField,
} from '../../../components/shared';
import { useFormik } from 'formik';
import { ModemType } from '../ModemDetails/data';
import useStyles from './style';
import { useNavigate } from 'react-router-dom';
import { routeConstants } from '../../../constants/routeConstants';
import { useAddModemMutation } from '../../../services/Modems/ModemsService';
import { useLazyGetAssetResyncStatusQuery } from '../../../services/Machines/MachinesService';
import { AddNewModemSchema } from '../../../schema/validationSchemas';
import { dateFormatForApi } from '../../../utils/common-methods';
import { useSelector } from 'react-redux';
import { useAssetSearch } from '../../../hooks/useAssetSearch';
import { useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const AddModem = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resyncWarning, setResyncWarning] = useState('');
  const [addModemApi] = useAddModemMutation();
  const [getAssetResyncStatus] = useLazyGetAssetResyncStatusQuery();
  const updatedBy = useSelector((state) => state.user?.userDetails?.id);
  const isResyncEnabled = process.env.REACT_APP_ENABLE_FEED_RESYNC === 'true';

  const {
    assetOptions: assetIds,
    isAssetError,
    handleInputChange: handleAssetIdSearch,
    getHelperText,
  } = useAssetSearch();

  const formik = useFormik({
    initialValues: {
      type: { text: '', value: '' },
      installedDate: '',
      inEffectFrom: '',
      assetId: { text: '', value: null },
      loginId: '',
      password: '',
      serialNumber: '',
    },
    validationSchema: AddNewModemSchema,
    onSubmit: async (data) => {
      setResyncWarning('');
      setIsLoading(true);

      if (isResyncEnabled && data.assetId?.value) {
        try {
          const resyncResult = await getAssetResyncStatus(data.assetId.value).unwrap();
          if (resyncResult?.data?.resync === true) {
            setResyncWarning(
              'A resync is currently in progress for this asset. Please try again after resync is complete.',
            );
            setIsLoading(false);
            return;
          }
        } catch {
          // If resync check fails, proceed with submission
        }
      }

      addModemApi({
        type: data.type?.value,
        installedDate: dateFormatForApi(data?.installedDate) || '',
        inEffectFrom: dateFormatForApi(data?.inEffectFrom) || '',
        assetId: data.assetId?.value || null,
        loginId: data?.loginId || '',
        password: data?.password || '',
        serialNumber: data?.serialNumber,
        updatedBy,
      })
        .unwrap()
        .then(() => {
          setSnackbarOpen(true);
          setTimeout(() => {
            navigate(`/${routeConstants.MODEMS_ROUTE}`, { replace: true });
          }, 1000);
        })
        .catch((error) => {
          setIsLoading(false);
          if (error.status === 409) {
            formik.setFieldError('serialNumber', `${error.data.message}`);
          }
        });
    },
  });
  const { values, errors, touched, getFieldProps, setFieldValue, handleSubmit } = formik;

  const onCancelHandler = () => {
    navigate(`/${routeConstants.MODEMS_ROUTE}`, { redirect: true });
  };

  return (
    <Box>
      <Box>
        <Typography variant="h1" pb={3}>
          Add Modem
        </Typography>
      </Box>
      {resyncWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setResyncWarning('')}>
          {resyncWarning}
        </Alert>
      )}
      <Box component={Paper} className={classes.formContent}>
        <form onSubmit={handleSubmit}>
          <GridContainer spacing={{ xs: 1, sm: 2, md: 3 }} alignItems="center">
            <GridItem xs={12} md={6}>
              <Autocomplete
                label="Type"
                data-testid="addModemFields"
                value={values.type}
                options={ModemType || []}
                getOptionLabel={(option) => option.text || ''}
                onChange={(e, value) => {
                  formik.setFieldValue('type', value);
                }}
                onBlur={() => formik.setFieldTouched('type')}
                error={Boolean(touched.type && errors.type)}
                helperText={touched.type && (typeof errors.type === 'string' ? errors.type : errors.type?.value)}
                disabled={isLoading}
              />
            </GridItem>
            <GridItem xs={12} md={6}>
              <Datepicker
                label="Installed Date"
                value={values.installedDate ? dayjs.utc(values.installedDate) : null}
                InputProps={{
                  'data-testid': 'addModemFields',
                }}
                inputFormat="MM/DD/YYYY"
                onChange={(newValue) => setFieldValue('installedDate', newValue === null ? '' : newValue)}
                onBlur={() => formik.setFieldTouched('installedDate')}
                error={Boolean(touched.installedDate && errors.installedDate)}
                helperText={touched.installedDate && errors.installedDate}
                disabled={isLoading}
              />
            </GridItem>
            <GridItem xs={12} md={6}>
              <Datepicker
                label="In Effect From"
                value={values.inEffectFrom ? dayjs.utc(values.inEffectFrom) : null}
                InputProps={{
                  'data-testid': 'addModemFields',
                  required: true,
                }}
                inputFormat="MM/DD/YYYY"
                maxDate={dayjs()}
                onChange={(newValue) => {
                  setFieldValue('inEffectFrom', newValue === null ? '' : newValue);
                }}
                onBlur={() => {
                  formik.setFieldTouched('inEffectFrom', true);
                }}
                error={Boolean(touched.inEffectFrom && errors.inEffectFrom)}
                helperText={touched.inEffectFrom && errors.inEffectFrom}
                disabled={isLoading}
              />
            </GridItem>
            <GridItem xs={12} md={6}>
              <Autocomplete
                label="Asset ID"
                data-testid="addModemFields"
                value={values.assetId}
                options={assetIds}
                getOptionLabel={(option) => option.text || ''}
                onChange={(e, value) => {
                  setFieldValue('assetId', value);
                }}
                onInputChange={(e, newValue) => {
                  handleAssetIdSearch(newValue);
                }}
                error={isAssetError}
                placeholder="Enter Asset ID number"
                helperText={getHelperText(values.assetId)}
                disabled={isLoading}
              />
              {!values.assetId?.value ? <FieldWarning field="Asset ID"></FieldWarning> : null}
            </GridItem>
            <GridItem xs={12} md={6}>
              <TextField
                label="Login ID"
                {...getFieldProps('loginId')}
                error={Boolean(touched.loginId && errors.loginId)}
                helperText={touched.loginId && errors.loginId}
                inputProps={{
                  'data-testid': 'addModemFields',
                }}
                disabled={isLoading}
              />
            </GridItem>
            <GridItem xs={12} md={6}>
              <TextField
                label="Password"
                {...getFieldProps('password')}
                error={Boolean(touched.password && errors.password)}
                helperText={touched.password && errors.password}
                inputProps={{
                  'data-testid': 'addModemFields',
                }}
                disabled={isLoading}
              />
            </GridItem>
            <GridItem xs={12} md={6}>
              <TextField
                label="Serial Number"
                {...getFieldProps('serialNumber')}
                error={Boolean(touched.serialNumber && errors.serialNumber)}
                helperText={touched.serialNumber && errors.serialNumber}
                inputProps={{
                  'data-testid': 'addModemFields',
                }}
                onChange={(e) => {
                  formik.setFieldValue('serialNumber', e.target.value.replace(/\s+/g, ''));
                }}
                disabled={isLoading}
              />
            </GridItem>
          </GridContainer>
          <GridContainer justifyContent="space-around" pl={4}>
            <GridItem xs={12} md={12}>
              <Button
                type="Submit"
                variant="contained"
                data-testid="addModemBtn"
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                <Typography variant="button">{isLoading ? 'CREATING...' : 'CREATE'}</Typography>
              </Button>
              <Button onClick={onCancelHandler} disabled={isLoading}>
                <Typography variant="button">CANCEL</Typography>
              </Button>
            </GridItem>
          </GridContainer>
        </form>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={1500}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert elevation={6} variant="filled" severity="success" onClose={() => setSnackbarOpen(false)}>
          Modem created successfully
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddModem;
